/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   fcjlogsService.ts
 *     Feature         :   138-dashboard-data-enrichment
 *     Description     :   API client and React Query hooks for FCJ Activity Logs
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-21
 * =========================================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../apiClient';
import type {
  FcjlogsSchemaStatus,
  FcjlogsRunSummary,
  FcjlogsStepSummary,
  FcjlogsActivityEntry,
  RunTrendPoint,
  StepDurationPoint,
  OffsetPaginationMeta,
} from '../../types/operations';

export const FCJLOGS_KEYS = {
  all: ['operations', 'fcjlogs'] as const,
  schemaStatus: () => [...FCJLOGS_KEYS.all, 'schema-status'] as const,
  runs: (filters: Record<string, any>) => [...FCJLOGS_KEYS.all, 'runs', filters] as const,
  runSteps: (runId: string) => [...FCJLOGS_KEYS.all, 'run-steps', runId] as const,
  stepEntries: (runId: string, stepName: string, filters: Record<string, any>) =>
    [...FCJLOGS_KEYS.all, 'step-entries', runId, stepName, filters] as const,
  analytics: (days: number) => [...FCJLOGS_KEYS.all, 'analytics', days] as const,
};

// ============================================================================
// API Calls
// ============================================================================

export async function getSchemaStatus(): Promise<FcjlogsSchemaStatus> {
  const response = await apiClient.get('/api/operations/fcjlogs/schema-status');
  return response.data.data;
}

export async function getRuns(limit: number, offset: number): Promise<{
  runs: FcjlogsRunSummary[];
  pagination: OffsetPaginationMeta;
}> {
  const response = await apiClient.get('/api/operations/fcjlogs/runs', { params: { limit, offset } });
  return response.data.data;
}

export async function getRunSteps(runId: string): Promise<{
  steps: FcjlogsStepSummary[];
}> {
  const response = await apiClient.get(`/api/operations/fcjlogs/runs/${encodeURIComponent(runId)}/steps`);
  return response.data.data;
}

export async function getStepEntries(
  runId: string,
  stepName: string,
  severity?: string,
  limit: number = 100,
  offset: number = 0,
): Promise<{
  entries: FcjlogsActivityEntry[];
  pagination: OffsetPaginationMeta;
}> {
  const params: Record<string, any> = { limit, offset };
  if (severity) params.severity = severity;
  const response = await apiClient.get(
    `/api/operations/fcjlogs/runs/${encodeURIComponent(runId)}/steps/${encodeURIComponent(stepName)}/entries`,
    { params },
  );
  return response.data.data;
}

export async function getAnalytics(days: number): Promise<{
  runTrends: RunTrendPoint[];
  stepDurations: StepDurationPoint[];
}> {
  const response = await apiClient.get('/api/operations/fcjlogs/analytics', { params: { days } });
  return response.data.data;
}

// ============================================================================
// React Query Hooks
// ============================================================================

export function useFcjlogsSchemaStatus() {
  return useQuery({
    queryKey: FCJLOGS_KEYS.schemaStatus(),
    queryFn: getSchemaStatus,
    staleTime: 300_000,  // 5 min — schema doesn't change often
    retry: 1,
  });
}

export function useFcjlogsRuns(limit: number = 50, offset: number = 0) {
  return useQuery({
    queryKey: FCJLOGS_KEYS.runs({ limit, offset }),
    queryFn: () => getRuns(limit, offset),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useFcjlogsRunSteps(runId: string | null) {
  return useQuery({
    queryKey: FCJLOGS_KEYS.runSteps(runId ?? ''),
    queryFn: () => getRunSteps(runId!),
    enabled: !!runId,
    staleTime: 30_000,
  });
}

export function useFcjlogsStepEntries(
  runId: string | null,
  stepName: string | null,
  severity?: string,
  limit: number = 100,
  offset: number = 0,
) {
  return useQuery({
    queryKey: FCJLOGS_KEYS.stepEntries(runId ?? '', stepName ?? '', { severity, limit, offset }),
    queryFn: () => getStepEntries(runId!, stepName!, severity, limit, offset),
    enabled: !!runId && !!stepName,
    staleTime: 30_000,
  });
}

export function useFcjlogsAnalytics(days: number = 30) {
  return useQuery({
    queryKey: FCJLOGS_KEYS.analytics(days),
    queryFn: () => getAnalytics(days),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}
